import React, { useCallback, useMemo } from 'react'
import { Button, Flex, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import useScreenSize from 'hooks/useScreenSize'
import { useSetupNetwork } from 'hooks/useSetupNetwork'
import { useTheme } from 'styled-components'
import { BigNumber } from 'ethers'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { LoadingPlaceholder } from './LoadingPlaceholder'

interface ClaimRewardButtonProps {
  claim: () => Promise<void>
  fetchAllData: (account?: string) => Promise<void>
  totalToClaim: BigNumber
  isLoading: boolean
  targetChainId: number
}

const ClaimRewardButton: React.FunctionComponent<ClaimRewardButtonProps> = ({
  claim,
  fetchAllData,
  totalToClaim,
  isLoading,
  targetChainId
}) => {
  const { account, chainId: currentChainId } = useWeb3React()
  const { t } = useTranslation()
  const { isMobile } = useScreenSize()
  const { colors } = useTheme()
  const { setupNetwork } = useSetupNetwork()
  const invalidNetwork = currentChainId !== targetChainId
  const nothingToClaim = totalToClaim?.isZero()

  const buttonProperties = useMemo(() => {
    if (invalidNetwork) {
      return {
        disabled: false,
        description: t('stakingPanel.switchNetwork')
      }
    }
    if (nothingToClaim) {
      return {
        disabled: true,
        description: t('stakingPanel.nothingToClaim')
      }
    }
    return {
      disabled: false,
      description: t('stakingPanel.claimReward')
    }
  }, [invalidNetwork, nothingToClaim, totalToClaim, isLoading])

  const handleClaimStake = useCallback(async () => {
    if (invalidNetwork) {
      await setupNetwork({ chainId: targetChainId, modal: true, error: false })
      return
    }
    await claim()
    await fetchAllData(account)
  }, [account, invalidNetwork, setupNetwork, targetChainId, claim, fetchAllData])

  return (
    <Flex fullWidth>
      {isLoading ? (
        <Flex flexDirection="column" gap="4px" alignItems={isMobile ? 'flex-start' : 'flex-end'}>
          <LoadingPlaceholder width="150px" height="30px" />
        </Flex>
      ) : (
        <Button
          fullWidth={isMobile}
          size={isMobile ? 'md' : 'sm'}
          disabled={buttonProperties.disabled}
          padding={isMobile ? '8px' : '16px 24px'}
          onClick={handleClaimStake}
        >
          <Text
            variant={TextVariant.BUTTON}
            color={buttonProperties.disabled ? colors.white4 : colors.white}
            style={{ cursor: buttonProperties.disabled ? 'not-allowed' : 'pointer' }}
          >
            {buttonProperties.description}
          </Text>
        </Button>
      )}
    </Flex>
  )
}

export default ClaimRewardButton
